import React, { useCallback } from "react";
import * as Mui from "@mui/material";
import { BranchingSection, Field } from "@/types/form";
import DeleteIcon from "@mui/icons-material/Delete";
import { FormAction } from "@/scripts/formAuthoringReducer";

type FieldOptionsProps = {
  dispatch: React.Dispatch<FormAction>;
  sectionId: string;
  field: Field;
  optionIndex: number;
  option: string;
  editEnabled: boolean;
  isBranchingField: boolean;
  branchingSections: BranchingSection[];
  branchingOptions?: string[];
};

function FieldOptions({
  dispatch,
  sectionId,
  field,
  optionIndex,
  option,
  editEnabled,
  isBranchingField,
  branchingOptions,
  branchingSections: sections,
}: FieldOptionsProps) {
  const updateFieldOption = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch({
        type: "UPDATE_FIELD_OPTION",
        payload: {
          sectionId,
          fieldId: field.id,
          optionIndex,
          value: e.target.value,
        },
      });
    },
    [dispatch, field.id, optionIndex, sectionId]
  );

  const updateBranchingOption = useCallback(
    (e: Mui.SelectChangeEvent<string>) => {
      dispatch({
        type: "UPDATE_BRANCHING_OPTION",
        payload: {
          sectionId,
          fieldId: field.id,
          optionIndex,
          nextSectionId: e.target.value,
        },
      });
    },
    [dispatch, field.id, optionIndex, sectionId]
  );

  const removeFieldOption = useCallback(() => {
    dispatch({
      type: "REMOVE_FIELD_OPTION",
      payload: {
        sectionId,
        fieldId: field.id,
        optionIndex,
      },
    });
  }, [dispatch, field.id, optionIndex, sectionId]);

  return (
    <Mui.Box key={optionIndex} display="flex" alignItems="center" margin={1}>
      {field.type === "radio" ? (
        <Mui.Radio disabled />
      ) : (
        <Mui.Checkbox disabled />
      )}
      <Mui.TextField
        value={option}
        onChange={updateFieldOption}
        disabled={!editEnabled}
        style={{ flexGrow: 2 }}
      />
      {isBranchingField && (
        <Mui.FormControl
          variant="outlined"
          style={{
            marginLeft: "10px",
            minWidth: "50px",
            maxWidth: "160px",
            flexGrow: 1,
          }}
        >
          <Mui.InputLabel>Next Section</Mui.InputLabel>
          <Mui.Select
            value={branchingOptions![optionIndex]}
            onChange={updateBranchingOption}
            disabled={!editEnabled}
            label="Next Section"
          >
            {sections
              .map((section) => (
                <Mui.MenuItem key={section.id} value={section.id}>
                  {section.title}
                </Mui.MenuItem>
              ))
              .concat(
                <Mui.MenuItem key="end" value="end">
                  End
                </Mui.MenuItem>
              )}
          </Mui.Select>
        </Mui.FormControl>
      )}
      {editEnabled && (
        <Mui.IconButton onClick={removeFieldOption} color="primary">
          <DeleteIcon />
        </Mui.IconButton>
      )}
    </Mui.Box>
  );
}

export default React.memo(FieldOptions);
